import React, { useState, useEffect } from "react";
import { BOARD_THEMES, PIECE_PALETTES, PIECE_DESIGNS, getPieceStyle, isUnlocked } from "../data/shopItems";
import { COSMETIC_ITEMS, getRarityLabel, getRarityColor } from "../data/cosmeticItems";
import { getPieceStyleFromCosmetic } from "../lib/effects";
import { getUserPurchases, getOwnedCosmetics } from "../lib/api";
import { DS } from "../theme";

const TABS = [
  ["boards",  "◈  Boards"],
  ["palette", "♟  Colours"],
  ["design",  "✦  Designs"],
  ["crown",   "♔  Crown"],
];

export default function Armoury({ prefs, onPrefsChange, stats, user }) {
  const [tab, setTab]             = useState("boards");
  const [purchasedIds, setPurchasedIds] = useState([]);
  const [loading, setLoading]     = useState(true);

  const earnedIds = (stats.earnedTrophies || []).map(t => t.id);

  useEffect(() => {
    if (!user?.token) { setLoading(false); return; }
    getUserPurchases(user.token).then(data => {
      if (data.purchases) setPurchasedIds(data.purchases.map(p => p.itemId));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const allBoards  = Object.values(BOARD_THEMES);
  const allPalettes = PIECE_PALETTES;
  const allDesigns  = PIECE_DESIGNS;

  const ownedBoards   = allBoards.filter(i   => isUnlocked(i, earnedIds, purchasedIds));
  const ownedPalettes = allPalettes.filter(i  => isUnlocked(i, earnedIds, purchasedIds));
  const ownedDesigns  = allDesigns.filter(i   => isUnlocked(i, earnedIds, purchasedIds));

  const totalOwned = ownedBoards.length + ownedPalettes.length + ownedDesigns.length;

  return (
    <div>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 20,
      }}>
        <div>
          <div className="cinzel" style={{ fontSize: 13, fontWeight: 700, color: DS.text, letterSpacing: "0.06em" }}>
            Your Locker
          </div>
          <div style={{ fontSize: 11, color: DS.textMuted, marginTop: 3 }}>
            {loading ? "Loading…" : `${totalOwned} item${totalOwned !== 1 ? "s" : ""} owned`}
          </div>
        </div>
        <div style={{ fontSize: 10, color: DS.textFaint }}>
          Visit the <strong style={{ color: DS.text }}>Shop</strong> to get more
        </div>
      </div>

      {/* Tab toggle */}
      <div style={{ display:"flex", background:DS.elevated, borderRadius:10, padding:4, marginBottom:16, gap:4 }}>
        {TABS.map(([t, lbl]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            flex:1, padding:"8px 0", border:"none", borderRadius:8,
            cursor:"pointer", fontFamily:"'Cinzel', Georgia, serif",
            fontWeight:600, fontSize:11, letterSpacing:"0.06em", transition:"all 0.15s",
            background: tab === t ? DS.accent : "transparent",
            color: tab === t ? "#fff" : DS.textMuted,
            boxShadow: tab === t ? "0 2px 8px rgba(115,102,240,0.3)" : "none",
          }}>{lbl}</button>
        ))}
      </div>

      {tab === "boards"  && <BoardsLocker  owned={ownedBoards}   all={allBoards}   prefs={prefs} onChange={onPrefsChange} />}
      {tab === "palette" && <PaletteLocker owned={ownedPalettes} all={allPalettes} prefs={prefs} onChange={onPrefsChange} />}
      {tab === "design"  && <DesignLocker  owned={ownedDesigns}  all={allDesigns}  prefs={prefs} onChange={onPrefsChange} />}
      {tab === "crown"   && <CrownLocker   prefs={prefs} onChange={onPrefsChange} user={user} />}
    </div>
  );
}

// ── BOARDS LOCKER ─────────────────────────────────────────────────────────────

function BoardsLocker({ owned, all, prefs, onChange }) {
  if (owned.length === 0) return <EmptyLocker type="boards" />;
  return (
    <div style={card}>
      <div style={label}>Board Themes — {owned.length}/{all.length} owned</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px,1fr))", gap:12 }}>
        {owned.map(theme => {
          const active = prefs.theme === theme.id;
          return (
            <div key={theme.id}
              onClick={() => onChange({ theme: theme.id })}
              style={{
                borderRadius:10, overflow:"hidden", cursor:"pointer",
                border:`2px solid ${active ? DS.accent : DS.borderFaint}`,
                background:DS.elevated,
                boxShadow: active ? `0 0 0 1px ${DS.accent}40` : "none",
              }}>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", height:52 }}>
                {Array.from({length:16},(_,i) => {
                  const r=Math.floor(i/4), c=i%4;
                  return <div key={i} style={{ background:(r+c)%2===0 ? theme.light : theme.dark }} />;
                })}
              </div>
              <div style={{ padding:"8px 10px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <span className="cinzel" style={{ fontSize:11, fontWeight:600, color:DS.text }}>{theme.name}</span>
                {active
                  ? <span style={{ color:DS.accent, fontSize:11, fontWeight:700 }}>✓</span>
                  : <span style={{ fontSize:10, color:DS.textFaint }}>Equip</span>
                }
              </div>
              {theme.unlockedBy && (
                <div style={{ padding:"0 10px 8px", fontSize:9, color:DS.gold }}>🏆 Trophy unlock</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── PALETTE LOCKER ────────────────────────────────────────────────────────────

function PaletteLocker({ owned, all, prefs, onChange }) {
  if (owned.length === 0) return <EmptyLocker type="colours" />;
  return (
    <div style={card}>
      <div style={label}>Piece Colours — {owned.length}/{all.length} owned</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(155px,1fr))", gap:12 }}>
        {owned.map(item => {
          const active = prefs.pieceColor === item.id;
          const wStyle = getPieceStyle(prefs.pieceDesign, item.id, true);
          const bStyle = getPieceStyle(prefs.pieceDesign, item.id, false);
          return (
            <div key={item.id}
              onClick={() => onChange({ pieceColor: item.id })}
              style={{
                background:DS.elevated, border:`2px solid ${active ? DS.accent : DS.borderFaint}`,
                borderRadius:10, padding:"14px 12px", cursor:"pointer", textAlign:"center",
                boxShadow: active ? `0 0 0 1px ${DS.accent}40` : "none",
              }}>
              <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:8 }}>
                <span style={{ fontSize:28, lineHeight:1, ...wStyle }}>♔</span>
                <span style={{ fontSize:28, lineHeight:1, ...bStyle }}>♚</span>
              </div>
              <div style={{ display:"flex", gap:5, justifyContent:"center", marginBottom:8 }}>
                <div style={{ width:10, height:10, borderRadius:"50%", background:item.light, border:`1px solid ${DS.border}` }} />
                <div style={{ width:10, height:10, borderRadius:"50%", background:item.dark,  border:`1px solid ${DS.border}` }} />
              </div>
              <div className="cinzel" style={{ fontWeight:700, fontSize:11, color:DS.text }}>{item.name}</div>
              <div style={{ fontSize:10, color:DS.textMuted, marginTop:3 }}>{item.desc}</div>
              {active
                ? <div className="cinzel" style={{ fontSize:10, color:DS.accent, fontWeight:700, marginTop:6 }}>Equipped ✓</div>
                : <div style={{ fontSize:10, color:DS.textFaint, marginTop:6 }}>Tap to equip</div>
              }
              {item.unlockedBy && (
                <div style={{ fontSize:9, color:DS.gold, marginTop:4 }}>🏆 Trophy unlock</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── DESIGN LOCKER ─────────────────────────────────────────────────────────────

function DesignLocker({ owned, all, prefs, onChange }) {
  if (owned.length === 0) return <EmptyLocker type="designs" />;
  return (
    <div style={card}>
      <div style={label}>Piece Designs — {owned.length}/{all.length} owned</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(155px,1fr))", gap:12 }}>
        {owned.map(design => {
          const active = prefs.pieceDesign === design.id;
          const wStyle = getPieceStyle(design.id, prefs.pieceColor, true);
          const bStyle = getPieceStyle(design.id, prefs.pieceColor, false);
          return (
            <div key={design.id}
              onClick={() => onChange({ pieceDesign: design.id })}
              style={{
                background:DS.elevated, border:`2px solid ${active ? DS.accent : DS.borderFaint}`,
                borderRadius:10, padding:"14px 12px", cursor:"pointer", textAlign:"center",
                boxShadow: active ? `0 0 0 1px ${DS.accent}40` : "none",
              }}>
              <div style={{
                display:"flex", gap:6, justifyContent:"center",
                background:"linear-gradient(135deg, #B58863 50%, #F0D9B5 50%)",
                borderRadius:6, padding:"8px 4px", marginBottom:10,
              }}>
                <span style={{ fontSize:32, lineHeight:1, ...wStyle }}>♔</span>
                <span style={{ fontSize:32, lineHeight:1, ...bStyle }}>♚</span>
              </div>
              <div className="cinzel" style={{ fontWeight:700, fontSize:11, color:DS.text }}>{design.name}</div>
              <div style={{ fontSize:10, color:DS.textMuted, marginTop:3 }}>{design.desc}</div>
              {active
                ? <div className="cinzel" style={{ fontSize:10, color:DS.accent, fontWeight:700, marginTop:6 }}>Equipped ✓</div>
                : <div style={{ fontSize:10, color:DS.textFaint, marginTop:6 }}>Tap to equip</div>
              }
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── CROWN LOCKER ─────────────────────────────────────────────────────────────

function CrownLocker({ prefs, onChange, user }) {
  const [ownedIds, setOwnedIds] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!user?.token) { setLoading(false); return; }
    getOwnedCosmetics(user.token)
      .then(data => setOwned(Array.isArray(data) ? data.map(d => d.itemId) : []))
      .catch(() => {})
      .finally(() => setLoading(false));

    function setOwned(ids) { setOwnedIds(ids); }
  }, [user]);

  if (loading) return <div style={{ textAlign:"center", padding:"32px 0", color:DS.textFaint, fontSize:13 }}>Loading…</div>;

  const ownedItems = COSMETIC_ITEMS.filter(c => ownedIds.includes(c.id));

  if (ownedItems.length === 0) return (
    <div style={{
      ...card, textAlign:"center", padding:"40px 24px",
      background:`linear-gradient(135deg, ${DS.elevated}, ${DS.surface})`,
    }}>
      <div style={{ fontSize:36, opacity:0.15, marginBottom:14 }}>♔</div>
      <div className="cinzel" style={{ fontSize:12, fontWeight:700, color:DS.textMuted, marginBottom:8 }}>
        No Crown cosmetics yet
      </div>
      <div style={{ fontSize:12, color:DS.textFaint, lineHeight:1.6 }}>
        Earn crowns through puzzles and games, then visit the Armoury Shop to unlock premium effects.
      </div>
    </div>
  );

  const pieceItems = ownedItems.filter(c => c.category === 'pieces');
  const boardItems = ownedItems.filter(c => c.category === 'board');

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {pieceItems.length > 0 && (
        <div style={card}>
          <div style={label}>Piece Sets</div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {pieceItems.map(item => {
              const equipped = prefs.activeCosmeticPieces === item.id;
              const wStyle = getPieceStyleFromCosmetic(item, true) || {};
              const bStyle = getPieceStyleFromCosmetic(item, false) || {};
              return (
                <div key={item.id} style={{
                  display:"flex", alignItems:"center", gap:14,
                  background:DS.elevated, border:`2px solid ${equipped ? DS.accent : DS.borderFaint}`,
                  borderRadius:12, padding:"14px 16px",
                  boxShadow: equipped ? `0 0 0 1px ${DS.accent}40` : "none",
                }}>
                  <div style={{ display:"flex", gap:4, flexShrink:0 }}>
                    <span style={{ fontSize:28, lineHeight:1, ...wStyle }}>♔</span>
                    <span style={{ fontSize:28, lineHeight:1, ...bStyle }}>♚</span>
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                      <span className="cinzel" style={{ fontSize:12, fontWeight:700, color:DS.text }}>{item.name}</span>
                      <span style={{ fontSize:9, color:getRarityColor(item.rarity), fontWeight:700,
                        background:`${getRarityColor(item.rarity)}18`, borderRadius:4, padding:"1px 5px" }}>
                        {getRarityLabel(item.rarity).toUpperCase()}
                      </span>
                    </div>
                    <div style={{ fontSize:11, color:DS.textMuted }}>{item.desc}</div>
                  </div>
                  <button className="cinzel" onClick={() => onChange({ activeCosmeticPieces: equipped ? null : item.id })} style={{
                    padding:"7px 14px", border:`1px solid ${equipped ? DS.accent : DS.borderFaint}`,
                    borderRadius:8, cursor:"pointer", fontSize:10, fontWeight:700, letterSpacing:"0.06em",
                    background: equipped ? `${DS.accent}18` : "transparent",
                    color: equipped ? DS.accent : DS.textMuted,
                    flexShrink:0,
                  }}>
                    {equipped ? "Equipped ✓" : "Equip"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {boardItems.length > 0 && (
        <div style={card}>
          <div style={label}>Board Effects</div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {boardItems.map(item => {
              const equipped = prefs.activeCosmeticBoard === item.id;
              const ls = item.preview?.lightSquare || "#F0D9B5";
              const ds2 = item.preview?.darkSquare  || "#B58863";
              return (
                <div key={item.id} style={{
                  display:"flex", alignItems:"center", gap:14,
                  background:DS.elevated, border:`2px solid ${equipped ? DS.accent : DS.borderFaint}`,
                  borderRadius:12, padding:"14px 16px",
                  boxShadow: equipped ? `0 0 0 1px ${DS.accent}40` : "none",
                }}>
                  <div style={{
                    display:"grid", gridTemplateColumns:"repeat(4,1fr)", width:52, height:52,
                    flexShrink:0, borderRadius:6, overflow:"hidden",
                    border:`1px solid ${DS.borderFaint}`,
                  }}>
                    {Array.from({length:16},(_,i) => {
                      const r=Math.floor(i/4), c=i%4;
                      return <div key={i} style={{ background:(r+c)%2===0 ? ls : ds2 }} />;
                    })}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
                      <span className="cinzel" style={{ fontSize:12, fontWeight:700, color:DS.text }}>{item.name}</span>
                      <span style={{ fontSize:9, color:getRarityColor(item.rarity), fontWeight:700,
                        background:`${getRarityColor(item.rarity)}18`, borderRadius:4, padding:"1px 5px" }}>
                        {getRarityLabel(item.rarity).toUpperCase()}
                      </span>
                    </div>
                    <div style={{ fontSize:11, color:DS.textMuted }}>{item.desc}</div>
                  </div>
                  <button className="cinzel" onClick={() => onChange({ activeCosmeticBoard: equipped ? null : item.id })} style={{
                    padding:"7px 14px", border:`1px solid ${equipped ? DS.accent : DS.borderFaint}`,
                    borderRadius:8, cursor:"pointer", fontSize:10, fontWeight:700, letterSpacing:"0.06em",
                    background: equipped ? `${DS.accent}18` : "transparent",
                    color: equipped ? DS.accent : DS.textMuted,
                    flexShrink:0,
                  }}>
                    {equipped ? "Equipped ✓" : "Equip"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── EMPTY STATE ───────────────────────────────────────────────────────────────

function EmptyLocker({ type }) {
  return (
    <div style={{
      ...card, textAlign: "center", padding: "40px 24px",
      background: `linear-gradient(135deg, ${DS.elevated}, ${DS.surface})`,
    }}>
      <div style={{ fontSize: 36, opacity: 0.15, marginBottom: 14 }}>◈</div>
      <div className="cinzel" style={{ fontSize: 12, fontWeight: 700, color: DS.textMuted, marginBottom: 8 }}>
        No {type} yet
      </div>
      <div style={{ fontSize: 12, color: DS.textFaint, lineHeight: 1.6 }}>
        Pick up items from the daily Shop or earn them through trophies.
      </div>
    </div>
  );
}

const card  = { background:DS.surface, border:`1px solid ${DS.borderFaint}`, borderRadius:DS.radius, padding:"18px 20px", marginBottom:16 };
const label = { fontFamily:"'Cinzel', Georgia, serif", fontSize:10, fontWeight:700, color:DS.textFaint, textTransform:"uppercase", letterSpacing:"0.1em", marginBottom:14 };
